import { useState } from 'react';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { CodesPanel } from '../components/CodesPanel';
import { LedgerPanel } from '../components/LedgerPanel';
import { ProgramsPanel } from '../components/ProgramsPanel';

const TABS = [
  { key: 'codes', label: 'Codes' },
  { key: 'rules', label: 'Reward rules' },
  { key: 'ledger', label: 'Reward ledger' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Referral program & incentive tracking (PE-2190, backend PE-2189).
 *
 * A referred prospect is attributed to a code from their contact page; when they sign or first
 * pay, osprey-api writes a ledger entry under the code's reward rule. TCS-staff only: gated on
 * REFERRAL_VIEW, writes on REFERRAL_MANAGE.
 */
export function ReferralsPage() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.REFERRAL_MANAGE);
  const [tab, setTab] = useState<TabKey>('codes');

  return (
    <div className="py-6 max-w-7xl mx-auto px-2">
      <div className="mb-6">
        <h1 className="text-[23px] font-bold text-foreground">Referrals</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Referral codes per referrer, what each referral earns, and every reward owed or paid.
        </p>
      </div>

      <div className="border-b border-border mb-6">
        <nav className="flex gap-6 overflow-x-auto" aria-label="Referral sections">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              aria-current={tab === t.key ? 'page' : undefined}
              className={`pb-3 min-h-10 text-sm transition-colors border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-foreground text-foreground font-medium'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'codes' && <CodesPanel canManage={canManage} />}
      {tab === 'rules' && <ProgramsPanel canManage={canManage} />}
      {tab === 'ledger' && <LedgerPanel canManage={canManage} />}
    </div>
  );
}
